/**
 * RepConnect - Placeholder Customer Service
 *
 * Handles creation and management of placeholder customer profiles
 * created by office staff during signup
 *
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 */

import { supabase } from './supabase';
import { logger } from './logger';

export class PlaceholderCustomerService {
  /**
   * Create a placeholder customer profile
   * Used when office staff manually enters a physician name or selects NPI-only physician
   *
   * @param {Object} customerData - Physician data from NPI or manual entry
   * @param {string} staffUserId - ID of staff member creating the placeholder
   * @returns {Promise<Object>} Created customer profile
   */
  static async createPlaceholderCustomer(customerData, staffUserId) {
    try {
      logger.debug('Creating placeholder customer via API', { customerData, staffUserId });

      // Call serverless API endpoint to create placeholder
      // This uses service role key to bypass RLS and auth requirements
      const response = await fetch('/api/create-placeholder-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerData,
          staffUserId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('API error creating placeholder customer', {
          status: response.status,
          error: errorData
        });
        throw new Error(errorData.details || errorData.error || 'Failed to create placeholder customer');
      }

      const { customer } = await response.json();
      logger.debug('Placeholder customer created successfully via API', {
        customerId: customer.id
      });

      return customer;

    } catch (error) {
      logger.error('Exception in createPlaceholderCustomer', { error: error.message });
      throw new Error(`Failed to create placeholder customer: ${error.message}`);
    }
  }

  /**
   * Link staff member as admin to customer profile
   * Creates entry in admin_users table
   *
   * @param {string} customerId - Customer's user profile ID
   * @param {string} staffId - Staff member's user profile ID
   * @param {string} role - Staff role (default: 'office_manager')
   * @returns {Promise<Object>} Created admin relationship
   */
  static async linkStaffAsAdmin(customerId, staffId, role = 'office_manager') {
    try {
      logger.debug('Linking staff as admin', { customerId, staffId, role });

      const adminData = {
        customer_id: customerId,
        staff_id: staffId,
        role: role,
        status: 'active', // Mark as active since staff is creating the relationship
        permissions: {
          manage_meetings: true,
          manage_availability: true,
          manage_preferences: true,
          manage_reps: true,
          view_reports: true,
          approve_meetings: true
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Insert into admin_users table
      const { data: adminRelationship, error } = await supabase
        .from('admin_users')
        .insert([adminData])
        .select()
        .single();

      if (error) {
        // Check if it's a duplicate error (staff already linked)
        if (error.code === '23505') {
          logger.warn('Staff already linked as admin', { customerId, staffId });
          // Fetch existing relationship
          const { data: existing } = await supabase
            .from('admin_users')
            .select('*')
            .eq('customer_id', customerId)
            .eq('staff_id', staffId)
            .single();
          return existing;
        }
        logger.error('Error linking staff as admin', { error: error.message });
        throw error;
      }

      logger.debug('Staff linked as admin successfully', { adminId: adminRelationship.id });
      return adminRelationship;

    } catch (error) {
      logger.error('Exception in linkStaffAsAdmin', { error: error.message });
      throw new Error(`Failed to link staff as admin: ${error.message}`);
    }
  }

  /**
   * Get staff members for a customer
   *
   * @param {string} customerId - Customer's user profile ID
   * @returns {Promise<Array>} Array of staff members with their details
   */
  static async getCustomerStaff(customerId) {
    try {
      logger.debug('Getting staff for customer', { customerId });

      const { data: staffList, error } = await supabase
        .from('admin_users')
        .select(`
          *,
          staff:staff_id (
            id,
            first_name,
            last_name,
            email,
            phone,
            title
          )
        `)
        .eq('customer_id', customerId);

      if (error) {
        logger.error('Error getting customer staff', { error: error.message });
        throw error;
      }

      logger.debug('Retrieved customer staff', { count: staffList?.length });
      return staffList || [];

    } catch (error) {
      logger.error('Exception in getCustomerStaff', { error: error.message });
      return [];
    }
  }

  /**
   * Get customers managed by a staff member
   *
   * @param {string} staffId - Staff member's user profile ID
   * @returns {Promise<Array>} Array of customers this staff manages
   */
  static async getStaffCustomers(staffId) {
    try {
      logger.debug('Getting customers for staff', { staffId });

      const { data: customerList, error } = await supabase
        .from('admin_users')
        .select(`
          *,
          customer:customer_id (
            id,
            first_name,
            last_name,
            email,
            phone,
            specialty,
            npi_number,
            company,
            account_status
          )
        `)
        .eq('staff_id', staffId);

      if (error) {
        logger.error('Error getting staff customers', { error: error.message });
        throw error;
      }

      logger.debug('Retrieved staff customers', { count: customerList?.length });
      return customerList || [];

    } catch (error) {
      logger.error('Exception in getStaffCustomers', { error: error.message });
      return [];
    }
  }

  /**
   * Check if a customer profile exists by name or NPI
   * Used for duplicate detection during signup
   *
   * @param {Object} searchCriteria - { firstName, lastName, npi_number, email }
   * @returns {Promise<Object|null>} Existing profile or null
   */
  static async findExistingCustomer(searchCriteria) {
    try {
      logger.debug('Searching for existing customer', { searchCriteria });

      const excludeId = searchCriteria.excludeUserId;

      // Search by NPI (exact match - highest priority)
      if (searchCriteria.npi_number) {
        let query = supabase
          .from('user_profiles')
          .select('*')
          .eq('user_type', 'customer')
          .eq('npi_number', searchCriteria.npi_number);

        if (excludeId) {
          query = query.neq('id', excludeId);
        }

        const { data, error } = await query.maybeSingle();
        if (!error && data) {
          logger.debug('Found customer by NPI', { customerId: data.id });
          return data;
        }
      }

      // Search by email (exact match) - Skip if searching placeholder emails
      if (searchCriteria.email && !searchCriteria.email.includes('placeholder')) {
        let query = supabase
          .from('user_profiles')
          .select('*')
          .eq('user_type', 'customer')
          .eq('email', searchCriteria.email);

        if (excludeId) {
          query = query.neq('id', excludeId);
        }

        const { data, error } = await query.maybeSingle();

        if (!error && data) {
          logger.debug('Found customer by email', { customerId: data.id });
          return data;
        }
      }

      // Search by name (fuzzy match)
      if (searchCriteria.firstName && searchCriteria.lastName) {
        let query = supabase
          .from('user_profiles')
          .select('*')
          .eq('user_type', 'customer')
          .ilike('first_name', `%${searchCriteria.firstName}%`)
          .ilike('last_name', `%${searchCriteria.lastName}%`);

        if (excludeId) {
          query = query.neq('id', excludeId);
        }

        const { data, error } = await query;

        if (!error && data && data.length > 0) {
          logger.debug('Found potential name matches', {
            count: data.length,
            firstName: searchCriteria.firstName,
            lastName: searchCriteria.lastName
          });
          return data[0]; // Return first match
        }
      }

      logger.debug('No existing customer found');
      return null;

    } catch (error) {
      logger.error('Exception in findExistingCustomer', { error: error.message });
      return null;
    }
  }

  /**
   * Claim an existing placeholder account
   * Merges physician's signup data with placeholder created by staff
   *
   * @param {string} placeholderId - ID of the placeholder profile to claim
   * @param {string} newUserId - ID of the new user's auth account (will be deleted)
   * @param {Object} physicianData - Physician's signup data to merge
   * @returns {Promise<Object>} Merged profile
   */
  static async claimPlaceholderAccount(placeholderId, newUserId, physicianData) {
    try {
      logger.debug('Claiming placeholder account via API', {
        placeholderId,
        newUserId,
        physicianEmail: physicianData.email
      });

      // Call serverless API endpoint to handle the merge
      // This uses service role key to:
      // 1. Update placeholder auth user with physician's credentials
      // 2. Merge profile data
      // 3. Delete the new physician's temporary auth user
      // 4. Preserve admin relationships
      const response = await fetch('/api/claim-placeholder-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          placeholderId,
          newUserId,
          physicianData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('API error claiming placeholder account', {
          status: response.status,
          error: errorData
        });
        throw new Error(errorData.details || errorData.error || 'Failed to claim placeholder account');
      }

      const { profile } = await response.json();
      logger.debug('Placeholder account claimed successfully via API', {
        profileId: profile.id
      });

      return profile;

    } catch (error) {
      logger.error('Exception in claimPlaceholderAccount', { error: error.message });
      throw new Error(`Failed to claim placeholder account: ${error.message}`);
    }
  }
}

export default PlaceholderCustomerService;
